import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Eye } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { useActiveTenant } from '../contexts/tenant'
import type { Product } from '@wsfull/shared'

type InboundOrder = {
  id: string; orderNumber: string; nfNumber: string | null
  status: string; expectedAt: string | null; createdAt: string
}
type ItemRow = { productId: string; quantityExpected: number }

const statusLabel: Record<string, string> = { pending: 'Pendente', receiving: 'Recebendo', received: 'Recebido', divergent: 'Divergente' }
const statusVariant: Record<string, 'default' | 'info' | 'warning' | 'success' | 'danger'> = {
  pending: 'default', receiving: 'info', received: 'success', divergent: 'warning',
}

export function InboundPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const tenantId = useActiveTenant()
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ orderNumber: '', nfNumber: '', nfKey: '', nfValue: '' })
  const [items, setItems] = useState<ItemRow[]>([{ productId: '', quantityExpected: 1 }])

  const { data: orders = [], isLoading } = useQuery<InboundOrder[]>({
    queryKey: ['inbound', tenantId],
    queryFn: async () => {
      if (!tenantId) return []
      return (await api.get('/inbound', { params: { tenantId } })).data
    },
    enabled: !!tenantId,
  })

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ['products', tenantId],
    queryFn: async () => (await api.get('/products', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  const create = useMutation({
    mutationFn: async () => {
      await api.post('/inbound', {
        tenantId,
        orderNumber: form.orderNumber,
        nfNumber: form.nfNumber || undefined,
        nfKey: form.nfKey || undefined,
        nfValue: form.nfValue ? parseFloat(form.nfValue) : undefined,
        items: items.filter(i => i.productId).map(i => ({ productId: i.productId, quantityExpected: i.quantityExpected })),
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbound', tenantId] })
      setShowForm(false)
      setForm({ orderNumber: '', nfNumber: '', nfKey: '', nfValue: '' })
      setItems([{ productId: '', quantityExpected: 1 }])
    },
  })

  const addItem = () => setItems(p => [...p, { productId: '', quantityExpected: 1 }])
  const removeItem = (i: number) => setItems(p => p.filter((_, idx) => idx !== i))
  const setItem = (i: number, k: keyof ItemRow, v: string | number) =>
    setItems(p => p.map((row, idx) => idx === i ? { ...row, [k]: v } : row))

  const canCreate = form.orderNumber && items.some(i => i.productId)

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Recebimento</h2>
          <p className="text-sm text-gray-500">Ordens de entrada de mercadoria</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate('/recebimento-paletes')} size="sm">Importar paletes (texto)</Button>
          <Button onClick={() => setShowForm(true)} size="sm"><Plus className="h-4 w-4" /> Nova ordem</Button>
        </div>
      </div>

      <Modal title="Nova ordem de recebimento" open={showForm} onClose={() => setShowForm(false)} size="lg">
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Número da ordem *" value={form.orderNumber} onChange={e => setForm({ ...form, orderNumber: e.target.value })} placeholder="NF-001" />
            <Input label="Número NF" value={form.nfNumber} onChange={e => setForm({ ...form, nfNumber: e.target.value })} placeholder="000001234" />
            <Input label="Valor NF (R$)" type="number" value={form.nfValue} onChange={e => setForm({ ...form, nfValue: e.target.value })} placeholder="0,00" />
            <Input label="Chave NF-e (44 dígitos)" value={form.nfKey} onChange={e => setForm({ ...form, nfKey: e.target.value })} />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Itens esperados *</label>
              <Button variant="ghost" size="sm" onClick={addItem}><Plus className="h-4 w-4" /> Adicionar</Button>
            </div>
            <div className="space-y-2">
              {items.map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <select
                    className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    value={item.productId}
                    onChange={e => setItem(i, 'productId', e.target.value)}
                  >
                    <option value="">Selecione o produto...</option>
                    {products.filter(p => p.active).map(p => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
                  </select>
                  <input
                    type="number" min={1}
                    className="w-24 rounded-lg border border-gray-200 px-3 py-2 text-sm text-right"
                    value={item.quantityExpected}
                    onChange={e => setItem(i, 'quantityExpected', parseInt(e.target.value) || 1)}
                  />
                  {items.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeItem(i)}>✕</Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {create.error && <p className="text-sm text-red-600">{(create.error as any)?.response?.data?.detail ?? (create.error as any)?.response?.data?.title ?? 'Erro ao criar'}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button loading={create.isPending} disabled={!canCreate} onClick={() => create.mutate()}>Criar ordem</Button>
          </div>
        </div>
      </Modal>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : orders.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Nenhuma ordem de recebimento</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Ordem</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">NF</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Criado em</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orders.map((o) => (
                    // A linha inteira abre o recebimento: clicar no olho minusculo
                    // da ponta era o unico caminho, e nao e onde a mao vai.
                    <tr key={o.id} onClick={() => navigate(`/inbound/${o.id}`)}
                      className="cursor-pointer hover:bg-blue-50/60">
                      <td className="px-5 py-3 font-medium text-gray-900">{o.orderNumber}</td>
                      <td className="px-5 py-3 text-gray-600">{o.nfNumber ?? '—'}</td>
                      <td className="px-5 py-3"><Badge variant={statusVariant[o.status] ?? 'default'}>{statusLabel[o.status] ?? o.status}</Badge></td>
                      <td className="px-5 py-3 text-gray-500">{new Date(o.createdAt).toLocaleDateString('pt-BR')}</td>
                      <td className="px-5 py-3">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/inbound/${o.id}`)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
